import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Typography,
} from '@growdash/design-system/atoms';
import {
  DollarSignIcon,
  MarketIcon,
  StarIcon,
  TruckIcon,
} from '@growdash/growintel/assets/icons';
import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import { Brand } from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { ArrowUpRight } from 'lucide-react';
import { Dispatch, SetStateAction } from 'react';
import BrandOverviewCard from '../components/brand-overview-card';
import BrandMapView from '../fragments/brand-location-insights';
import BrandOverViewSkeleton from '../fragments/brand-overview-skeleton';

type Props = {
  brand: Brand;
  isBrandLoading: boolean;
  setActiveTab: Dispatch<SetStateAction<'overview' | 'menu' | 'branches'>>;
};

export default function BrandOverviewView({
  brand,
  isBrandLoading,
  setActiveTab,
}: Props) {
  const {
    cuisines,
    brand_image,
    median_price,
    avg_price,
    avg_delivery_time,
    total_items,
    total_branches,
    avg_rating_score,
  } = brand;
  const cuisineArray = cuisines?.split(',') ?? [];

  const currency = useCurrency();

  if (!isBrandLoading && !brand.id) return null;

  if (isBrandLoading) return <BrandOverViewSkeleton />;

  return (
    <div className="bg-quarternary p-4 animate-fade-in">
      <Card>
        <CardContent>
          <div className="flex gap-6">
            <div className="flex flex-col gap-4 max-w-[6.375rem]">
              <GrowintelImage
                type="brand"
                className="w-full h-[6.375rem] object-contain shadow-image rounded-2xl"
                src={brand_image ?? ''}
              />

              <div className="lg:w-[150%] flex flex-wrap gap-2">
                {cuisineArray.map((cuisine) => (
                  <Badge key={cuisine} variant="secondary" className="text-xs">
                    {cuisine}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4  basis-full">
              <BrandOverviewCard
                icon={StarIcon}
                title="Rating"
                value={avg_rating_score ? Math.max(0, avg_rating_score) : 'N/A'}
              />

              <BrandOverviewCard
                icon={TruckIcon}
                title="Avg. Delivery Time"
                value={
                  avg_delivery_time && avg_delivery_time > 0
                    ? `${avg_delivery_time?.toFixed(1)} minutes`
                    : 'N/A'
                }
              />
              <BrandOverviewCard
                icon={DollarSignIcon}
                title="Avg Price"
                value={
                  avg_price ? `${currency} ${avg_price?.toFixed(2)}` : 'N/A'
                }
              />
              <BrandOverviewCard
                icon={() => <MarketIcon />}
                title="Branches"
                value={`${total_branches} locations`}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="w-full grid md:grid-cols-12 mt-4 gap-x-4">
        <div className="flex flex-col gap-4 md:col-span-4">
          <Card>
            <CardHeader>
              <CardTitle>Brand Details</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <Typography className="text-sm font-bold">
                  Total Branches
                </Typography>
                <div className="flex items-center gap-2 text-sm mt-2">
                  <p>{`${total_branches} branches`}</p>
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <p className="select-none font-semibold flex items-center gap-0.5 underline text-bg_primary opacity-60">
                          View Map <ArrowUpRight size={16} />
                        </p>
                      </TooltipTrigger>
                      <TooltipContent>Coming Soon 🔥</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <div>
                <Typography className="text-sm font-bold">
                  Menu Items
                </Typography>
                <div className="flex items-center gap-2 text-sm mt-2">
                  <p>{`${total_items} total items`}</p>
                  <p
                    onClick={() => {
                      setActiveTab('menu');
                    }}
                    className="cursor-pointer font-semibold flex items-center gap-0.5 underline text-bg_primary"
                  >
                    Open Menu <ArrowUpRight size={16} />
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col gap-4 md:col-span-8">
          <Card>
            <CardHeader>
              <CardTitle>Menu Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <p className="text-sm font-medium">Price Range</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Progress
                      value={median_price}
                      max={100}
                      className="h-2 bg-tertiary [&>div]:bg-foreground"
                    />
                  </div>
                  <p className="ml-auto text-sm text-end text-xxs text-secondary">
                    Median: {median_price} {currency}
                  </p>
                </div>

                {avg_rating_score && avg_rating_score > 0 && (
                  <div>
                    <div className="flex justify-between mb-2">
                      <p className="text-sm font-medium">
                        Customer Satisfaction
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Progress
                        value={(Math.max(0, avg_rating_score) / 5) * 100}
                        className="h-2 bg-tertiary [&>div]:bg-foreground"
                      />
                    </div>
                    <p className="ml-auto text-sm text-end text-xxs text-secondary">
                      {Math.max(0, avg_rating_score)} / 5
                    </p>
                  </div>
                )}
                {avg_delivery_time && (
                  <div>
                    <div className="flex justify-between mb-2">
                      <p className="text-sm font-medium">Delivery Speed</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Progress
                        value={100 - (avg_delivery_time / 60) * 100}
                        className="h-2 bg-tertiary [&>div]:bg-foreground"
                      />
                    </div>
                    <p className="ml-auto text-sm text-end text-xxs text-secondary">
                      {avg_delivery_time.toFixed(2)} min
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <BrandMapView />
        </div>
      </div>
    </div>
  );
}
